// SCR-STAFF-993 — مرکز تماس، بخش ثبت دستی (US-CHN-032 فاز 2؛ AC-CHN-032.1؛ AP-CHN-02): ثبت تماس با جهت، شماره (فقط چهار رقم آخر نمایش و Hash ذخیره)، مراجعه‌کننده،
// علت، نتیجه، مدت و یادداشت و اقدام سریع پس از ثبت: رزرو نوبت (SCH)، ثبت سرنخ (ENG) یا ثبت تیکت پیوسته به تماس. فهرست: تماس‌های خود (chn.call.handle) یا همه
// (chn.call.view_all). پنجره تماس‌گیرنده، صف تماس برگشتی و اتصال VoIP فاز 4 است.
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type ChnCallDirection,
  type ChnCallLogRequest,
  type ChnCallOutcome,
  type ChnCallReason,
  type ChnCallView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { LeadDialog } from '../engagement/LeadDialog';
import { PatientPicker } from '../patients/PatientPicker';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { WriteNotice } from '../settings/common';
import { chnTicketKeys, CreateTicketDialog, SCR_STAFF_993, ticketPath, type TicketPrefill } from './ticketing';

const DIRECTIONS: readonly ChnCallDirection[] = ['Inbound', 'Outbound'];
const REASONS: readonly ChnCallReason[] = ['Booking', 'Cancellation', 'PriceInquiry', 'ResultFollowUp', 'Complaint', 'Other'];
const OUTCOMES: readonly ChnCallOutcome[] = ['Informed', 'Booked', 'AppointmentChanged', 'LeadCreated', 'CallbackNeeded', 'NoAnswer'];

function duration(seconds: number | string): string {
  const total = Number(seconds);
  const minutes = Math.floor(total / 60);
  return faDigits(`${minutes}:${String(total % 60).padStart(2, '0')}`);
}

export function CallsPage() {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('chn.call.handle');
  const [reason, setReason] = useState<ChnCallReason | ''>('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [logging, setLogging] = useState(false);
  const [last, setLast] = useState<ChnCallView | null>(null);
  const calls = useQuery({
    queryKey: [...chnTicketKeys.calls(session?.tenantId), reason, cursor],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/channels/calls', { params: { query: { reason: reason || undefined, cursor, limit: 50 } } })),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<ChnCallView>[] = [
    { id: 'at', headerKey: 'chn:calls.columns.at', cell: (row) => <JalaliDate value={row.occurredAt} withTime /> },
    { id: 'direction', headerKey: 'chn:calls.columns.direction', cell: (row) => t(`chn:calls.direction.${row.direction}`) },
    { id: 'phone', headerKey: 'chn:calls.columns.phone', cell: (row) => (row.phoneLast4 ? <bdi dir="ltr">…{faDigits(row.phoneLast4)}</bdi> : '—') },
    { id: 'patient', headerKey: 'chn:calls.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'reason', headerKey: 'chn:calls.columns.reason', cell: (row) => t(`chn:calls.reason.${row.reason}`) },
    { id: 'outcome', headerKey: 'chn:calls.columns.outcome', cell: (row) => <span data-testid={`chn-call-outcome-${row.id}`}>{t(`chn:calls.outcome.${row.outcome}`)}</span> },
    { id: 'duration', headerKey: 'chn:calls.columns.duration', cell: (row) => <bdi>{duration(row.durationSeconds)}</bdi> },
    { id: 'by', headerKey: 'chn:calls.columns.by', cell: (row) => row.handledByName },
    { id: 'actions', headerKey: 'chn:calls.columns.actions', cell: (row) => <QuickActions call={row} compact /> },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_993}
      titleKey="chn:calls.title"
      subtitleKey="chn:calls.subtitle"
      actions={
        write.permitted ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setLogging(true)} disabled={!write.canWrite} data-testid="chn-call-new">
            {t('chn:calls.new')}
          </Button>
        ) : null
      }
      filters={
        <Stack spacing={1}>
          {write.permitted ? <WriteNotice write={write} /> : null}
          <TextField select size="small" label={t('chn:calls.columns.reason')} value={reason} onChange={(event) => { setCursor(undefined); setReason(event.target.value as ChnCallReason | ''); }} sx={{ maxWidth: 240 }}>
            <MenuItem value="">{t('chn:calls.all_reasons')}</MenuItem>
            {REASONS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`chn:calls.reason.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {last ? (
            <Alert severity="success" onClose={() => setLast(null)} data-testid="chn-call-logged" action={<QuickActions call={last} />}>
              {t('chn:calls.logged')}
            </Alert>
          ) : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="chn:calls.title"
        columns={columns}
        rows={calls.data?.items}
        getRowId={(row) => row.id}
        isLoading={calls.isPending}
        error={calls.error}
        onRetry={() => void calls.refetch()}
        hasMore={Boolean(calls.data?.nextCursor)}
        onLoadMore={() => setCursor(calls.data?.nextCursor ?? undefined)}
        emptyTitleKey="chn:calls.empty"
      />
      {logging ? (
        <LogCallDialog
          onClose={(call) => {
            setLogging(false);
            if (call) {
              setLast(call);
            }
          }}
        />
      ) : null}
    </ListPage>
  );
}

/** اقدام سریع از تماس (AC-CHN-032.1): رزرو، ثبت سرنخ، ثبت تیکت. */
function QuickActions({ call, compact = false }: { call: ChnCallView; compact?: boolean }) {
  const { t } = useTranslation('chn');
  const access = useAccess();
  const scheduling = useSchedulingActions();
  const navigate = useNavigate();
  const [lead, setLead] = useState(false);
  const [ticket, setTicket] = useState<TicketPrefill | null>(null);
  const size = compact ? 'small' : 'medium';
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {scheduling?.canBook && call.patientId ? (
        <Button size={size} onClick={() => scheduling.openBooking({ patient: { id: call.patientId ?? '', displayName: call.patientName ?? '' } })} data-testid={`chn-call-book-${call.id}`}>
          {t('chn:calls.quick.book')}
        </Button>
      ) : null}
      {access.can('eng.lead.manage') && !call.patientId ? (
        <Button size={size} onClick={() => setLead(true)} data-testid={`chn-call-lead-${call.id}`}>
          {t('chn:calls.quick.lead')}
        </Button>
      ) : null}
      {call.ticketId ? (
        <Button size={size} onClick={() => navigate(ticketPath(call.ticketId ?? ''))} data-testid={`chn-call-open-ticket-${call.id}`}>
          {t('chn:calls.quick.open_ticket')}
        </Button>
      ) : access.can('chn.ticket.create') ? (
        <Button
          size={size}
          onClick={() =>
            setTicket({
              source: 'Phone',
              callId: call.id,
              patient: call.patientId ? { id: call.patientId, displayName: call.patientName ?? '' } : null,
              subject: t('chn:calls.quick.ticket_subject', { reason: t(`chn:calls.reason.${call.reason}`) }),
            })
          }
          data-testid={`chn-call-ticket-${call.id}`}
        >
          {t('chn:calls.quick.ticket')}
        </Button>
      ) : null}
      {lead ? <LeadDialog onClose={() => setLead(false)} /> : null}
      {ticket ? (
        <CreateTicketDialog
          prefill={ticket}
          onClose={(created) => {
            setTicket(null);
            if (created) {
              void navigate(ticketPath(created.ticket.id));
            }
          }}
        />
      ) : null}
    </Stack>
  );
}

function LogCallDialog({ onClose }: { onClose: (call?: ChnCallView) => void }) {
  const { t } = useTranslation('chn');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [direction, setDirection] = useState<ChnCallDirection>('Inbound');
  const [phone, setPhone] = useState('');
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const [reason, setReason] = useState<ChnCallReason>('Booking');
  const [outcome, setOutcome] = useState<ChnCallOutcome>('Informed');
  const [minutes, setMinutes] = useState('1');
  const [note, setNote] = useState('');
  const log = useMutation({
    mutationFn: () => {
      const body: ChnCallLogRequest = {
        direction,
        phone: phone.trim() || null,
        patientId: patient?.id ?? null,
        leadId: null,
        branchId: null,
        reason,
        outcome,
        durationSeconds: Math.max(0, Math.round(Number(minutes || '0') * 60)),
        note: note.trim() || null,
        occurredAt: null,
      };
      return unwrap(tenantApi.POST('/api/v1/channels/calls', { body, headers: { 'Idempotency-Key': idempotency.current() } }));
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (call) => {
      void queryClient.invalidateQueries({ queryKey: chnTicketKeys.calls(session?.tenantId) });
      onClose(call);
    },
  });
  return (
    <FormDialog
      open
      titleKey="chn:calls.new"
      screenId={SCR_STAFF_993}
      onSubmit={() => log.mutate()}
      onClose={() => onClose()}
      isDirty={Boolean(phone || note || patient)}
      isSubmitting={log.isPending}
      submitError={log.error}
      submitKey="chn:calls.submit"
      maxWidth="md"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField select label={t('chn:calls.fields.direction')} value={direction} onChange={(event) => setDirection(event.target.value as ChnCallDirection)} fullWidth>
          {DIRECTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`chn:calls.direction.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField label={t('chn:calls.fields.phone')} value={phone} onChange={(event) => setPhone(event.target.value)} fullWidth slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel', maxLength: 20, 'data-testid': 'chn-call-phone' } }} />
      </Stack>
      <PatientPicker labelKey="chn:calls.fields.patient" value={patient} onChange={setPatient} testId="chn-call-patient" />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField select label={t('chn:calls.fields.reason')} value={reason} onChange={(event) => setReason(event.target.value as ChnCallReason)} fullWidth slotProps={{ htmlInput: { 'data-testid': 'chn-call-reason' } }}>
          {REASONS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`chn:calls.reason.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label={t('chn:calls.fields.outcome')} value={outcome} onChange={(event) => setOutcome(event.target.value as ChnCallOutcome)} fullWidth slotProps={{ htmlInput: { 'data-testid': 'chn-call-outcome' } }}>
          {OUTCOMES.map((value) => (
            <MenuItem key={value} value={value} disabled={value === 'NoAnswer' && direction === 'Inbound'}>
              {t(`chn:calls.outcome.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField type="number" label={t('chn:calls.fields.duration')} value={minutes} onChange={(event) => setMinutes(event.target.value)} sx={{ minWidth: 140 }} slotProps={{ htmlInput: { min: 0, step: 0.5, 'data-testid': 'chn-call-duration' } }} />
      </Stack>
      <TextField multiline minRows={2} label={t('chn:calls.fields.note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'chn-call-note' } }} />
    </FormDialog>
  );
}
